import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { ProjectServiceConnector } from '@/modules/api/ProjectApiConnector';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { ConfirmDialog } from '@/modules/common/components/ConfirmDialog';

import { useFloorplanAdminStore } from './store';
import { logger } from '@common/logging/logger';

type Props = {
  isOpen: boolean;
  onCloseClick: () => void;
};

export function FloorplanDeleter({ isOpen, onCloseClick }: Props) {
  const { t } = useTranslation(['interface', 'common']);
  const selectedFloorplanIds = useFloorplanAdminStore((state) => state.selectedFloorplanIdsState);
  const selectedAmount = selectedFloorplanIds.length;
  const { showSnackbar } = useSnackbarStore();
  const { projectId } = useParams();

  const handleConfirm = useCallback(async () => {
    if (!projectId) {
      logger.error('projectId is undefined.');
      return;
    }

    const {
      selectedFloorplanIdsState: deleteIds,
      setSelectedFloorplanIdsState,
      setFloorplanIdsBeingDeletedState,
    } = useFloorplanAdminStore.getState();

    if (!deleteIds.length) {
      onCloseClick();
      return;
    }

    setFloorplanIdsBeingDeletedState(deleteIds);
    onCloseClick();

    try {
      const results = await Promise.all(
        deleteIds.map((id: string) =>
          ProjectServiceConnector.getInstance()
            .deleteLayoutAlternative({
              projectId,
              alternativeId: id,
            })
            .then(() => ({ result: 'success', alternativeId: id, error: null }))
            .catch((e) => ({ result: 'failed', alternativeId: id, error: e })),
        ),
      );

      const failedReqs = results.filter((result) => result.result === 'failed');
      const succeededReqs = results.filter((result) => result.result === 'success');

      if (succeededReqs.length) {
        const deleteFloorPlanGroupIds = succeededReqs.map((req) => req.alternativeId);

        const { floorPlanGroups, setFloorPlanGroups } = useFloorplanAdminStore.getState();
        setFloorPlanGroups(
          floorPlanGroups.filter((item) => !deleteFloorPlanGroupIds.includes(item.alternativeId)),
        );
      }

      if (failedReqs.length) {
        const floorplansIds = failedReqs.map((req) => req.alternativeId);

        setSelectedFloorplanIdsState(floorplansIds);
        showSnackbar(t('errors:home.delete_floorplan'));

        return;
      }

      setSelectedFloorplanIdsState([]);

      showSnackbar(
        t('interface:home.delete_floorplan.notification.success', 'Deleted successfully'),
      );
    } catch (e) {
      logger.error('Something unexpected happened while deleing floorplan. Error: ', e);
    } finally {
      setFloorplanIdsBeingDeletedState([]);
    }
  }, [projectId, showSnackbar, onCloseClick, t]);

  return (
    <ConfirmDialog
      title={
        selectedAmount > 1
          ? t('interface:home.delete_floorplan.title.plural', {
              amount: selectedAmount,
            })
          : t('interface:home.delete_floorplan.title.single')
      }
      content={
        selectedAmount > 1
          ? t('interface:home.delete_floorplan.subtitle.plural', {
              amount: selectedAmount,
            })
          : t('interface:home.delete_floorplan.subtitle.single')
      }
      yesLabel={t('common:confirm', 'Confirm')}
      noLabel={t('common:cancel', 'Cancel')}
      open={isOpen}
      onClose={onCloseClick}
      onConfirm={handleConfirm}
      onDecline={onCloseClick}
    />
  );
}
